import { forwardRef, useState, useEffect, useContext, useMemo } from 'react';
import type { Ref, ReactNode, MouseEvent, UIEvent } from 'react';

import BareButton from '../Button/BareButton';
import Popover from '../Popover';
import type { PopoverProps } from '../Popover';
import Menu from '../Menu';
import type { MenuItemProps } from '../Menu';
import type { AcceptedMouseEventElement } from '../Menu/Menu.types';
import { useConsolidatedRef, useI18n } from '../../hooks';
import type { AsProp, OmitStrict } from '../../types';
import Tooltip from '../Tooltip';

import AppShellContext from './AppShellContext';
import type { OperatorProps } from './AppShell.types';
import { StyledAppShellTooltip } from './AppShell.styles';

const Operator = forwardRef(function Operator(
  {
    children,
    actions,
    popover,
    name,
    as
  }: {
    children: ReactNode;
    actions: OperatorProps['actions'];
    popover?: OmitStrict<PopoverProps, 'show' | 'target' | 'children'>;
    name: OperatorProps['name'];
  } & AsProp,
  ref: Ref<HTMLButtonElement> | undefined
) {
  const { navOpen } = useContext(AppShellContext);
  const buttonRef = useConsolidatedRef(ref);
  const t = useI18n();

  const operatorMenuItems = useMemo(() => {
    return actions.flat().map<MenuItemProps>(({ id, primary, onClick: click, ...rest }) => ({
      id: id || primary,
      primary,
      onClick: (menuId, e) => click?.(e),
      ...rest
    }));
  }, [actions]);

  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    if (!navOpen) setShowMenu(false);
  }, [navOpen]);

  return (
    <>
      <BareButton
        as={as}
        ref={buttonRef}
        aria-label={`${t('app_shell_user_account')} - ${name}`}
        aria-expanded={showMenu}
        aria-haspopup='menu'
        onClick={() => {
          setShowMenu(cur => !cur);
        }}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'Escape') setShowMenu(false);
        }}
        onBlur={() => {
          setShowMenu(false);
        }}
      >
        {children}
      </BareButton>
      <Tooltip
        target={buttonRef.current}
        as={StyledAppShellTooltip}
        showDelay='none'
        hideDelay='none'
      >
        {t('app_shell_user_account')}
      </Tooltip>
      <Popover {...popover} show={showMenu} target={buttonRef.current}>
        <Menu
          focusControlEl={buttonRef.current ?? undefined}
          mode='action'
          items={operatorMenuItems}
          /** @see {@link MenuButton} implementation for explanation of conditions. This is in essence duplicated. */
          onItemClick={(_, e) => {
            if (
              (e as MouseEvent<AcceptedMouseEventElement> & UIEvent<AcceptedMouseEventElement>)
                .detail > 0
            ) {
              setShowMenu(false);
            }
          }}
        />
      </Popover>
    </>
  );
});

export default Operator;
